'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Star,
  Plus,
  X,
  ChevronDown,
  Check,
  Award,
  Clock,
  Package,
  DollarSign,
  Building2,
  Globe,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  AlertCircle,
  BarChart2,
} from 'lucide-react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Supplier {
  id: string;
  name: string;
  category: string;
  products: string[];
  address: string;
  phone: string;
  email: string;
  website: string;
  certifications: string[];
  minOrder: string;
  minOrderValue: number;
  leadTimeDays: number;
  leadTime: string;
  rating: number;
  priceIndex: number; // 1–5 scale (1=cheapest)
  responseTime: string;
  yearsActive: number;
  onTimeDelivery: number; // percentage
}

// ─── DATA ────────────────────────────────────────────────────────────────────

const allSuppliers: Supplier[] = [
  {
    id: 's1',
    name: 'Thai Union Group PCL',
    category: 'Seafood & Fish Products',
    products: ['Canned Tuna', 'Frozen Shrimp', 'Processed Seafood', 'Fish Sauce'],
    address: '72/1 Moo 7, Sethakit 1 Rd, Samut Sakhon 74000',
    phone: '+66 34 417 222',
    email: 'export@thaiunion.com',
    website: 'www.thaiunion.com',
    certifications: ['ISO 22000', 'BRC', 'MSC', 'ASC'],
    minOrder: 'USD 10,000',
    minOrderValue: 10000,
    leadTimeDays: 37,
    leadTime: '30–45 days',
    rating: 4.8,
    priceIndex: 3,
    responseTime: '< 24 hrs',
    yearsActive: 28,
    onTimeDelivery: 96,
  },
  {
    id: 's2',
    name: 'Hana Microelectronics',
    category: 'Electronic Components',
    products: ['PCBs', 'Semiconductor Assemblies', 'Electronic Modules', 'LED Components'],
    address: '605 Moo 5, Bangpoo Industrial Estate, Samut Prakan 10280',
    phone: '+66 2 709 9000',
    email: 'sales@hana.com',
    website: 'www.hana.com',
    certifications: ['ISO 9001', 'ISO 14001', 'IATF 16949'],
    minOrder: 'USD 25,000',
    minOrderValue: 25000,
    leadTimeDays: 52,
    leadTime: '45–60 days',
    rating: 4.6,
    priceIndex: 4,
    responseTime: '< 48 hrs',
    yearsActive: 35,
    onTimeDelivery: 93,
  },
  {
    id: 's3',
    name: 'Sri Trang Agro-Industry',
    category: 'Rubber Products',
    products: ['Natural Rubber', 'Latex Concentrate', 'Rubber Gloves', 'Rubber Sheets'],
    address: '10 Soi 10, Phetkasem Rd, Hat Yai, Songkhla 90110',
    phone: '+66 74 471 491',
    email: 'export@sritrang.com',
    website: 'www.sritrang.com',
    certifications: ['ISO 9001', 'FSC', 'RSPO'],
    minOrder: 'USD 15,000',
    minOrderValue: 15000,
    leadTimeDays: 25,
    leadTime: '21–30 days',
    rating: 4.7,
    priceIndex: 2,
    responseTime: '< 24 hrs',
    yearsActive: 42,
    onTimeDelivery: 97,
  },
  {
    id: 's4',
    name: 'Charoen Pokphand Foods',
    category: 'Tropical Fruits & Processed Foods',
    products: ['Frozen Durian', 'Mango Pulp', 'Pineapple Products', 'Tapioca Starch'],
    address: 'CP Tower, 313 Silom Rd, Bangrak, Bangkok 10500',
    phone: '+66 2 638 2000',
    email: 'international@cpf.co.th',
    website: 'www.cpfworldwide.com',
    certifications: ['ISO 22000', 'HACCP', 'GlobalGAP'],
    minOrder: 'USD 20,000',
    minOrderValue: 20000,
    leadTimeDays: 31,
    leadTime: '28–35 days',
    rating: 4.5,
    priceIndex: 3,
    responseTime: '< 72 hrs',
    yearsActive: 50,
    onTimeDelivery: 91,
  },
  {
    id: 's5',
    name: 'PTT Global Chemical',
    category: 'Chemical & Plastic Materials',
    products: ['Polyethylene', 'Polypropylene', 'Specialty Chemicals', 'Petrochemicals'],
    address: '555/1 Energy Complex Bldg A, Vibhavadi Rangsit Rd, Bangkok 10900',
    phone: '+66 2 265 8400',
    email: 'export@pttgc.com',
    website: 'www.pttgcgroup.com',
    certifications: ['ISO 9001', 'ISO 14001', 'OHSAS 18001'],
    minOrder: 'USD 50,000',
    minOrderValue: 50000,
    leadTimeDays: 37,
    leadTime: '30–45 days',
    rating: 4.4,
    priceIndex: 5,
    responseTime: '< 48 hrs',
    yearsActive: 15,
    onTimeDelivery: 89,
  },
  {
    id: 's6',
    name: 'Mitr Phol Sugar Corp',
    category: 'Processed Tapioca & Sugar',
    products: ['Tapioca Chips', 'Tapioca Starch', 'Refined Sugar', 'Molasses'],
    address: '2 Mitr Phol Tower, Ratchadaphisek Rd, Bangkok 10310',
    phone: '+66 2 694 2000',
    email: 'trade@mitrphol.com',
    website: 'www.mitrphol.com',
    certifications: ['ISO 9001', 'Bonsucro', 'FSSC 22000'],
    minOrder: 'USD 12,000',
    minOrderValue: 12000,
    leadTimeDays: 24,
    leadTime: '21–28 days',
    rating: 4.3,
    priceIndex: 2,
    responseTime: '< 24 hrs',
    yearsActive: 60,
    onTimeDelivery: 94,
  },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function StarRating({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
        />
      ))}
      <span className="ml-1 text-sm font-semibold text-foreground">{value.toFixed(1)}</span>
    </div>
  );
}

function PriceBar({ value, isBest }: { value: number; isBest: boolean }) {
  const labels = ['', 'Very Low', 'Low', 'Moderate', 'High', 'Premium'];
  const widths = ['0%', '20%', '40%', '60%', '80%', '100%'];
  const colors = ['', '#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium" style={{ color: colors[value] }}>
          {labels[value]}
        </span>
        {isBest && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
            Best Price
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: widths[value], backgroundColor: colors[value] }}
        />
      </div>
    </div>
  );
}

function LeadTimeBar({ days, isBest }: { days: number; isBest: boolean }) {
  const max = 60;
  const pct = Math.min((days / max) * 100, 100);
  const color = days <= 25 ? '#22c55e' : days <= 35 ? '#f59e0b' : '#ef4444';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold" style={{ color }}>
          ~{days} days
        </span>
        {isBest && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
            Fastest
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function MOQBar({ value, isBest }: { value: number; isBest: boolean }) {
  const max = 50000;
  const pct = Math.min((value / max) * 100, 100);
  const color = value <= 12000 ? '#22c55e' : value <= 20000 ? '#f59e0b' : '#ef4444';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold" style={{ color }}>
          USD {value.toLocaleString()}
        </span>
        {isBest && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
            Lowest MOQ
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// ─── SUPPLIER SELECTOR ───────────────────────────────────────────────────────

interface SelectorProps {
  selected: Supplier | null;
  onSelect: (s: Supplier | null) => void;
  excluded: string[];
  slotIndex: number;
}

function SupplierSelector({ selected, onSelect, excluded, slotIndex }: SelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = allSuppliers.filter(
    (s) => !excluded.includes(s.id) && s.name.toLowerCase().includes(query.toLowerCase())
  );

  const slotColors = ['#4F6EF7', '#F59E0B', '#10B981', '#8B5CF6'];
  const color = slotColors[slotIndex % slotColors.length];

  if (selected) {
    return (
      <div
        className="relative rounded-xl border-2 p-1"
        style={{ borderColor: color + '33' }}
      >
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ backgroundColor: color + '10' }}
        >
          <div
            className="h-7 w-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
            style={{ backgroundColor: color }}
          >
            {selected.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{selected.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{selected.category}</p>
          </div>
          <button
            onClick={() => onSelect(null)}
            className="shrink-0 p-1 rounded-md hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150 text-muted-foreground hover:text-primary group"
      >
        <div className="h-7 w-7 rounded-lg border-2 border-dashed border-current flex items-center justify-center">
          <Plus size={14} />
        </div>
        <span className="text-sm font-medium">Add Supplier</span>
        <ChevronDown size={14} className="ml-auto" />
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search suppliers…"
              className="w-full px-3 py-2 text-sm bg-muted rounded-lg outline-none placeholder:text-muted-foreground"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto scrollbar-thin py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-muted-foreground text-center">No suppliers available</li>
            ) : (
              filtered.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => { onSelect(s); setOpen(false); setQuery(''); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left"
                  >
                    <div
                      className="h-7 w-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      {s.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{s.category}</p>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── COMPARISON ROW ──────────────────────────────────────────────────────────

interface RowProps {
  label: string;
  icon: React.ReactNode;
  suppliers: (Supplier | null)[];
  render: (s: Supplier, isBest: boolean) => React.ReactNode;
  getBest?: (suppliers: Supplier[]) => string;
}

function ComparisonRow({ label, icon, suppliers, render, getBest }: RowProps) {
  const active = suppliers.filter(Boolean) as Supplier[];
  const bestId = getBest && active.length > 1 ? getBest(active) : null;

  return (
    <div className="grid gap-px" style={{ gridTemplateColumns: `180px repeat(${suppliers.length}, 1fr)` }}>
      {/* Label */}
      <div className="flex items-center gap-2 px-4 py-4 bg-muted/40 border-b border-border">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-sm font-semibold text-foreground">{label}</span>
      </div>
      {/* Values */}
      {suppliers.map((s, i) => (
        <div key={i} className="px-5 py-4 bg-card border-b border-l border-border">
          {s ? render(s, bestId === s.id) : (
            <div className="h-6 w-24 rounded bg-muted animate-pulse" />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

const MAX_SLOTS = 4;

export default function SupplierComparePage() {
  const [slots, setSlots] = useState<(Supplier | null)[]>([null, null]);

  const selectedIds = slots.filter(Boolean).map((s) => s!.id);
  const activeSuppliers = slots.filter(Boolean) as Supplier[];

  const handleSelect = (index: number, supplier: Supplier | null) => {
    setSlots((prev) => {
      const next = [...prev];
      next[index] = supplier;
      return next;
    });
  };

  const addSlot = () => {
    if (slots.length < MAX_SLOTS) setSlots((prev) => [...prev, null]);
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  // Best-value helpers
  const bestRating = (list: Supplier[]) => list.reduce((a, b) => (a.rating > b.rating ? a : b)).id;
  const bestPrice = (list: Supplier[]) => list.reduce((a, b) => (a.priceIndex < b.priceIndex ? a : b)).id;
  const bestMOQ = (list: Supplier[]) => list.reduce((a, b) => (a.minOrderValue < b.minOrderValue ? a : b)).id;
  const bestLead = (list: Supplier[]) => list.reduce((a, b) => (a.leadTimeDays < b.leadTimeDays ? a : b)).id;
  const bestDelivery = (list: Supplier[]) => list.reduce((a, b) => (a.onTimeDelivery > b.onTimeDelivery ? a : b)).id;

  const slotColors = ['#4F6EF7', '#F59E0B', '#10B981', '#8B5CF6'];

  // Score card: count "best" wins per supplier
  const scoreMap = useMemo(() => {
    if (activeSuppliers.length < 2) return {};
    const map: Record<string, number> = {};
    [bestRating, bestPrice, bestMOQ, bestLead, bestDelivery].forEach((fn) => {
      const id = fn(activeSuppliers);
      map[id] = (map[id] || 0) + 1;
    });
    return map;
  }, [activeSuppliers]);

  const hasSelections = activeSuppliers.length >= 2;

  return (
    <AppLayout>
      <div className="flex flex-col h-full overflow-y-auto scrollbar-thin bg-background">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border bg-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight">Supplier Comparison</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Select up to {MAX_SLOTS} suppliers to compare side-by-side across key sourcing criteria
              </p>
            </div>
            {slots.length < MAX_SLOTS && (
              <button
                onClick={addSlot}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors btn-press"
              >
                <Plus size={15} />
                Add Slot
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 p-6 space-y-6">
          {/* Selector Row */}
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${slots.length}, 1fr)` }}>
            {slots.map((s, i) => (
              <div key={i} className="relative">
                <SupplierSelector
                  selected={s}
                  onSelect={(val) => handleSelect(i, val)}
                  excluded={selectedIds.filter((id) => id !== s?.id)}
                  slotIndex={i}
                />
                {slots.length > 2 && (
                  <button
                    onClick={() => removeSlot(i)}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-100 text-red-500 hover:bg-red-200 flex items-center justify-center transition-colors z-10"
                    title="Remove slot"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Empty State */}
          {!hasSelections && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <BarChart2 size={28} className="text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">Select at least 2 suppliers</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Use the dropdowns above to pick suppliers and see a detailed side-by-side comparison.
              </p>
            </div>
          )}

          {/* Score Summary */}
          {hasSelections && (
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${slots.length}, 1fr)` }}>
              {slots.map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl border-2 p-4 bg-card"
                  style={{ borderColor: s ? slotColors[i % slotColors.length] + '40' : 'var(--border)' }}
                >
                  {s ? (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-bold text-foreground leading-tight">{s.name}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{s.category}</p>
                        </div>
                        {scoreMap[s.id] > 0 && (
                          <div
                            className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-white text-[11px] font-bold"
                            style={{ backgroundColor: slotColors[i % slotColors.length] }}
                          >
                            <TrendingUp size={11} />
                            {scoreMap[s.id]} best
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <StarRating value={s.rating} />
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {s.certifications.slice(0, 3).map((c) => (
                          <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-md bg-secondary text-secondary-foreground font-medium">
                            {c}
                          </span>
                        ))}
                        {s.certifications.length > 3 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-medium">
                            +{s.certifications.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-20 text-muted-foreground">
                      <AlertCircle size={20} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Comparison Table */}
          {hasSelections && (
            <div className="rounded-xl border border-border overflow-hidden bg-card shadow-sm">
              {/* Table Header */}
              <div
                className="grid gap-px"
                style={{ gridTemplateColumns: `180px repeat(${slots.length}, 1fr)` }}
              >
                <div className="px-4 py-3 bg-muted/60 border-b border-border">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Criteria
                  </span>
                </div>
                {slots.map((s, i) => (
                  <div
                    key={i}
                    className="px-5 py-3 border-b border-l border-border"
                    style={{ backgroundColor: s ? slotColors[i % slotColors.length] + '08' : 'var(--muted)' }}
                  >
                    {s ? (
                      <div className="flex items-center gap-2">
                        <div
                          className="h-5 w-5 rounded-md flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                          style={{ backgroundColor: slotColors[i % slotColors.length] }}
                        >
                          {s.name.charAt(0)}
                        </div>
                        <span className="text-xs font-semibold text-foreground truncate">{s.name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Rating Row */}
              <ComparisonRow
                label="Rating"
                icon={<Star size={15} />}
                suppliers={slots}
                getBest={bestRating}
                render={(s, isBest) => (
                  <div className="flex items-center gap-2">
                    <StarRating value={s.rating} />
                    {isBest && <Check size={14} className="text-green-500 shrink-0" />}
                  </div>
                )}
              />

              {/* Pricing Row */}
              <ComparisonRow
                label="Price Level"
                icon={<DollarSign size={15} />}
                suppliers={slots}
                getBest={bestPrice}
                render={(s, isBest) => <PriceBar value={s.priceIndex} isBest={isBest} />}
              />

              {/* MOQ Row */}
              <ComparisonRow
                label="Min. Order (MOQ)"
                icon={<Package size={15} />}
                suppliers={slots}
                getBest={bestMOQ}
                render={(s, isBest) => <MOQBar value={s.minOrderValue} isBest={isBest} />}
              />

              {/* Lead Time Row */}
              <ComparisonRow
                label="Lead Time"
                icon={<Clock size={15} />}
                suppliers={slots}
                getBest={bestLead}
                render={(s, isBest) => <LeadTimeBar days={s.leadTimeDays} isBest={isBest} />}
              />

              {/* On-Time Delivery Row */}
              <ComparisonRow
                label="On-Time Delivery"
                icon={<TrendingUp size={15} />}
                suppliers={slots}
                getBest={bestDelivery}
                render={(s, isBest) => (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-semibold"
                        style={{ color: s.onTimeDelivery >= 95 ? '#22c55e' : s.onTimeDelivery >= 90 ? '#f59e0b' : '#ef4444' }}
                      >
                        {s.onTimeDelivery}%
                      </span>
                      {isBest && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
                          Best
                        </span>
                      )}
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${s.onTimeDelivery}%`,
                          backgroundColor: s.onTimeDelivery >= 95 ? '#22c55e' : s.onTimeDelivery >= 90 ? '#f59e0b' : '#ef4444',
                        }}
                      />
                    </div>
                  </div>
                )}
              />

              {/* Certifications Row */}
              <ComparisonRow
                label="Certifications"
                icon={<Award size={15} />}
                suppliers={slots}
                render={(s) => (
                  <div className="flex flex-wrap gap-1">
                    {s.certifications.map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-md bg-secondary text-secondary-foreground font-semibold">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              />

              {/* Products Row */}
              <ComparisonRow
                label="Key Products"
                icon={<Building2 size={15} />}
                suppliers={slots}
                render={(s) => (
                  <div className="flex flex-wrap gap-1">
                    {s.products.slice(0, 3).map((p) => (
                      <span key={p} className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-medium">
                        {p}
                      </span>
                    ))}
                    {s.products.length > 3 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-medium">
                        +{s.products.length - 3}
                      </span>
                    )}
                  </div>
                )}
              />

              {/* Response Time Row */}
              <ComparisonRow
                label="Response Time"
                icon={<Clock size={15} />}
                suppliers={slots}
                render={(s) => (
                  <span className="text-sm font-medium text-foreground">{s.responseTime}</span>
                )}
              />

              {/* Years Active Row */}
              <ComparisonRow
                label="Years Active"
                icon={<TrendingUp size={15} />}
                suppliers={slots}
                render={(s) => (
                  <span className="text-sm font-medium text-foreground">{s.yearsActive} years</span>
                )}
              />

              {/* Contact Row */}
              <ComparisonRow
                label="Contact"
                icon={<Phone size={15} />}
                suppliers={slots}
                render={(s) => (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Phone size={11} />
                      <span className="truncate">{s.phone}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail size={11} />
                      <span className="truncate">{s.email}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-primary">
                      <Globe size={11} />
                      <span className="truncate">{s.website}</span>
                    </div>
                  </div>
                )}
              />

              {/* Location Row */}
              <ComparisonRow
                label="Location"
                icon={<MapPin size={15} />}
                suppliers={slots}
                render={(s) => (
                  <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={11} className="shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{s.address}</span>
                  </div>
                )}
              />
            </div>
          )}

          {/* Decision Helper */}
          {hasSelections && activeSuppliers.length >= 2 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <TrendingUp size={15} className="text-primary" />
                Sourcing Decision Summary
              </h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {[
                  { label: 'Best Rating', fn: bestRating, icon: <Star size={13} /> },
                  { label: 'Best Price', fn: bestPrice, icon: <DollarSign size={13} /> },
                  { label: 'Lowest MOQ', fn: bestMOQ, icon: <Package size={13} /> },
                  { label: 'Fastest Lead', fn: bestLead, icon: <Clock size={13} /> },
                  { label: 'Best Delivery', fn: bestDelivery, icon: <TrendingUp size={13} /> },
                ].map(({ label, fn, icon }) => {
                  const winnerId = fn(activeSuppliers);
                  const winner = activeSuppliers.find((s) => s.id === winnerId);
                  const winnerIdx = slots.findIndex((s) => s?.id === winnerId);
                  return (
                    <div key={label} className="rounded-lg bg-muted/50 p-3 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        {icon}
                        <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
                      </div>
                      {winner && (
                        <div className="flex items-center gap-1.5">
                          <div
                            className="h-4 w-4 rounded flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                            style={{ backgroundColor: slotColors[winnerIdx % slotColors.length] }}
                          >
                            {winner.name.charAt(0)}
                          </div>
                          <span className="text-xs font-semibold text-foreground truncate">{winner.name.split(' ')[0]}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
